import { ReactNode, useEffect } from "react";

import type { AsidePanelConfig, MainPanelConfig, TopbarConfig } from "@/panels/modules/panels/store/panels.store.types";

import { PanelsView } from "@/panels/modules/panels/components/PanelsView";
import {
    setAsidePanelConfig,
    setLeftMainPanelDefaultSize,
    setMainPanelConfig,
    setTopbarConfig,
    useAsidePanelConfig,
    useLeftMainPanelDefaultSize,
    useMainPanelConfig,
    useTopbarConfig,
} from "@/panels/modules/panels/store/panels.store.selectors";

import { useAsidePanelConfigSync, useMainPanelConfigSync, useTopbarConfigSync } from "../hooks/usePanelConfigSync";

interface PanelsProps {
    // Slot content
    leftSidebarSlot?: ReactNode;
    topbarSlot?: ReactNode;
    rightSidebarSlot?: ReactNode;
    leftTopASlot?: ReactNode;
    leftTopBSlot?: ReactNode;
    leftBottomASlot?: ReactNode;
    leftBottomBSlot?: ReactNode;
    rightTopASlot?: ReactNode;
    rightTopBSlot?: ReactNode;
    rightBottomASlot?: ReactNode;
    rightBottomBSlot?: ReactNode;

    // Incoming main panel configs
    leftSidebarConfig?: Partial<AsidePanelConfig>;
    topbarConfig?: Partial<TopbarConfig>;
    rightSidebarConfig?: Partial<AsidePanelConfig>;
    leftTopConfig?: Partial<MainPanelConfig>;
    leftBottomConfig?: Partial<MainPanelConfig>;
    rightTopConfig?: Partial<MainPanelConfig>;
    rightBottomConfig?: Partial<MainPanelConfig>;

    // Incoming global panel configs
    leftMainPanelDefaultSize?: number;
}

export const Panels = ({
    leftSidebarSlot,
    topbarSlot,
    rightSidebarSlot,
    leftTopASlot,
    leftTopBSlot,
    leftBottomASlot,
    leftBottomBSlot,
    rightTopASlot,
    rightTopBSlot,
    rightBottomASlot,
    rightBottomBSlot,

    leftSidebarConfig,
    topbarConfig,
    rightSidebarConfig,
    leftTopConfig,
    leftBottomConfig,
    rightTopConfig,
    rightBottomConfig,

    leftMainPanelDefaultSize,
}: PanelsProps) => {
    // Get full configs from store
    const leftSidebarStoreConfig = useAsidePanelConfig("leftSidebar");
    const topbarStoreConfig = useTopbarConfig();
    const rightSidebarStoreConfig = useAsidePanelConfig("rightSidebar");
    const leftTopStoreConfig = useMainPanelConfig("leftTop");
    const leftBottomStoreConfig = useMainPanelConfig("leftBottom");
    const rightTopStoreConfig = useMainPanelConfig("rightTop");
    const rightBottomStoreConfig = useMainPanelConfig("rightBottom");

    const leftMainPanelDefaultSizeFromStore = useLeftMainPanelDefaultSize();

    useAsidePanelConfigSync(leftSidebarConfig, "leftSidebar", setAsidePanelConfig);
    useTopbarConfigSync(topbarConfig, setTopbarConfig);
    useAsidePanelConfigSync(rightSidebarConfig, "rightSidebar", setAsidePanelConfig);
    useMainPanelConfigSync(leftTopConfig, "leftTop", setMainPanelConfig);
    useMainPanelConfigSync(leftBottomConfig, "leftBottom", setMainPanelConfig);
    useMainPanelConfigSync(rightTopConfig, "rightTop", setMainPanelConfig);
    useMainPanelConfigSync(rightBottomConfig, "rightBottom", setMainPanelConfig);

    useEffect(() => {
        if (typeof leftMainPanelDefaultSize !== "number") return;
        setLeftMainPanelDefaultSize(leftMainPanelDefaultSize);
    }, [leftMainPanelDefaultSize]);

    return (
        <PanelsView
            leftSidebar={leftSidebarSlot}
            topbar={topbarSlot}
            rightSidebar={rightSidebarSlot}
            leftTopA={leftTopASlot}
            leftTopB={leftTopBSlot}
            leftBottomA={leftBottomASlot}
            leftBottomB={leftBottomBSlot}
            rightTopA={rightTopASlot}
            rightTopB={rightTopBSlot}
            rightBottomA={rightBottomASlot}
            rightBottomB={rightBottomBSlot}
            leftSidebarConfig={leftSidebarStoreConfig}
            topbarConfig={topbarStoreConfig}
            rightSidebarConfig={rightSidebarStoreConfig}
            leftTopConfig={leftTopStoreConfig}
            leftBottomConfig={leftBottomStoreConfig}
            rightTopConfig={rightTopStoreConfig}
            rightBottomConfig={rightBottomStoreConfig}
            leftMainPanelDefaultSize={leftMainPanelDefaultSizeFromStore}
        />
    );
};
